import ServicePageLayout from './ServicePageLayout';
import { Shield, Search, FileCheck, TrendingUp, Users, AlertTriangle, CheckCircle, BarChart3, Target, Lightbulb } from 'lucide-react';

const relatedServices = [
  { path: '/conseil-rh/', label: 'Conseil RH Particuliers' },
  { path: '/droit-social/', label: 'Expertise Droit Social' },
  { path: '/formation-rh/', label: 'Formations RH' },
  { path: '/accompagnement-collectivites/', label: 'Accompagnement Collectivites' },
];

const auditTypes = [
  {
    icon: Shield,
    title: "Audit de conformite juridique",
    description: "Verification exhaustive de la conformite de vos pratiques RH avec le Code du travail et les conventions collectives applicables. Analyse des contrats de travail, des bulletins de paie, des registres obligatoires et de l ensemble des documents RH. Identification des ecarts et proposition d un plan de mise en conformite priorise.",
  },
  {
    icon: Search,
    title: "Audit organisationnel et processus",
    description: "Evaluation de l efficacite de vos processus RH : recrutement, integration, evaluation, formation, gestion des carrieres. Cartographie des flux de travail, identification des goulots d etranglement et recommandations d optimisation pour gagner en efficacite operationnelle.",
  },
  {
    icon: FileCheck,
    title: "Audit des pratiques de remuneration",
    description: "Analyse de votre politique de remuneration : coherence interne, competitivite externe, respect de l egalite de traitement. Evaluation de la structure des remunerations, des avantages sociaux et des dispositifs d interessement. Recommandations pour optimiser votre masse salariale tout en restant attractif.",
  },
  {
    icon: TrendingUp,
    title: "Audit de la politique QVT",
    description: "Evaluation de votre politique Qualite de Vie au Travail : diagnostic des risques psychosociaux, analyse des indicateurs d absenteeisme et de turnover, evaluation des dispositifs existants. Proposition d un plan d action QVT mesurable et adapte a votre culture d entreprise.",
  },
  {
    icon: Users,
    title: "Audit social et climat social",
    description: "Mesure du climat social par enquete anonyme, entretiens individuels et analyse des indicateurs sociaux. Identification des facteurs de satisfaction et d insatisfaction, evaluation de la qualite du management et des relations sociales. Rapport detaille avec preconisations concretes.",
  },
  {
    icon: AlertTriangle,
    title: "Audit de prevention contentieuse",
    description: "Identification proactive des situations a risque contentieux : contentieux en cours, situations individuelles sensibles, pratiques a risque. Evaluation financiere des risques et elaboration d une strategie de prevention et de gestion des litiges existants et potentiels.",
  },
];

const methodology = [
  {
    step: "1",
    title: "Cadrage et definition des objectifs",
    desc: "Nous definissons ensemble le perimetre de l audit, les objectifs attendus et le calendrier. Cette phase inclut la collecte des documents prealables et la planification des entretiens.",
  },
  {
    step: "2",
    title: "Collecte des donnees sur le terrain",
    desc: "Analyse documentaire, entretiens individuels et collectifs, observation des pratiques. Je m adapte a votre organisation pour minimiser la perturbation du fonctionnement quotidien.",
  },
  {
    step: "3",
    title: "Analyse et diagnostic",
    desc: "Traitement et analyse des donnees collectees. Croisement des indicateurs quantitatifs et qualitatifs pour etablir un diagnostic complet et objectif de votre situation RH.",
  },
  {
    step: "4",
    title: "Restitution et plan d action",
    desc: "Presentation des resultats avec recommandations priorisees. Elaboration d un plan d action realiste avec echancier, budget previsionnel et indicateurs de suivi.",
  },
  {
    step: "5",
    title: "Suivi et accompagnement",
    desc: "Accompagnement dans la mise en oeuvre des recommandations. Points d etape reguliers pour ajuster le plan d action et mesurer les progres realises.",
  },
];

export default function AuditRHPage() {
  return (
    <ServicePageLayout
      title="Audit RH et Diagnostic Social | Aurore RH"
      description="Audit RH complet pour entreprises et collectivites : conformite juridique, organisation, QVT et climat social."
      canonical="https://aurorerh.fr/audit-rh/"
      relatedServices={relatedServices}
    >
      <div className="prose dark:prose-invert max-w-none mb-16">
        <p className="text-lg text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed mb-6">
          L audit RH est un outil strategique pour evaluer l etat de sante de votre fonction RH et identifier 
          les leviers d amelioration. Que vous souhaitiez verifier votre conformite juridique, optimiser vos 
          processus ou ameliorer le climat social, un audit approfondi vous donne une vision claire et objective 
          de votre situation.
        </p>
        <p className="text-lg text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed mb-6">
          Mon approche de l audit RH est a la fois rigoureuse et pragmatique. Je ne me contente pas de dresser 
          un constat : je vous accompagne dans la comprehension des enjeux, la priorisation des actions et 
          la mise en oeuvre concrete des recommandations. Chaque audit aboutit a un plan d action realiste, 
          chiffre et echelonne dans le temps.
        </p>
      </div>

      <h2 className="text-3xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-10">
        Nos types d audit RH
      </h2>

      <div className="space-y-8 mb-16">
        {auditTypes.map((audit, index) => (
          <div
            key={index}
            className="flex gap-5 p-6 bg-white dark:bg-warm-gray-800 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-aurore-light/20 dark:border-warm-gray-700"
          >
            <div className="flex-shrink-0 w-14 h-14 bg-aurore-light/40 dark:bg-warm-gray-700 rounded-xl flex items-center justify-center">
              <audit.icon className="w-7 h-7 text-aurore-dark dark:text-aurore-light" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-warm-gray-900 dark:text-warm-gray-100 mb-2">
                {audit.title}
              </h3>
              <p className="text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed">
                {audit.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      <h2 className="text-3xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-10">
        Notre methodologie d audit en 5 etapes
      </h2>

      <div className="space-y-6 mb-16">
        {methodology.map((item, index) => (
          <div key={index} className="flex gap-5">
            <div className="flex-shrink-0 w-12 h-12 bg-aurore-dark text-white rounded-full flex items-center justify-center text-lg font-bold">
              {item.step}
            </div>
            <div className="pb-6 border-l-2 border-aurore-light dark:border-warm-gray-700 pl-6 -ml-6 ml-0">
              <h3 className="text-lg font-semibold text-warm-gray-900 dark:text-warm-gray-100 mb-2">
                {item.title}
              </h3>
              <p className="text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed">
                {item.desc}
              </p>
            </div>
          </div>
        ))}
      </div>

      <h2 className="text-3xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-6">
        Les benefices d un audit RH
      </h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
        {[
          { icon: CheckCircle, text: "Reduction des risques juridiques et contentieux" },
          { icon: BarChart3, text: "Optimisation de la masse salariale" },
          { icon: Target, text: "Amelioration de la performance RH" },
          { icon: Users, text: "Meilleur climat social et engagement" },
          { icon: Lightbulb, text: "Identification des leviers d amelioration" },
          { icon: Shield, text: "Securisation juridique des pratiques" },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-3 p-4 bg-warm-gray-50 dark:bg-warm-gray-800/50 rounded-xl">
            <item.icon className="w-5 h-5 text-aurore-dark dark:text-aurore-light flex-shrink-0" />
            <span className="text-warm-gray-700 dark:text-warm-gray-300 text-sm">{item.text}</span>
          </div>
        ))}
      </div>

      <h2 className="text-3xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-6">
        Quand realiser un audit RH ?
      </h2>
      <div className="space-y-3 mb-16">
        {[
          "Avant une croissance ou un recrutement massif",
          "Lors d une fusion, acquisition ou restructuration",
          "En cas d augmentation des contentieux ou de l absenteeisme",
          "Avant une negociation collective ou un accord d entreprise",
          "Lors d un changement de direction des RH",
          "De maniere preventive, tous les 2 a 3 ans",
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-3">
            <div className="flex-shrink-0 w-2 h-2 bg-aurore-dark rounded-full mt-2" />
            <p className="text-warm-gray-700 dark:text-warm-gray-300">{item}</p>
          </div>
        ))}
      </div>

      <div className="bg-gradient-to-br from-aurore-pale to-aurore-light/30 dark:from-warm-gray-800 dark:to-warm-gray-800 rounded-2xl p-8">
        <h3 className="text-xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-4">
          Rapport d audit confidentiel et personnalise
        </h3>
        <p className="text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed">
          Chaque audit donne lieu a un rapport detaille et confidentiel, comprenant le diagnostic, les 
          recommandations priorisees, le plan d action avec echancier et budget previsionnel. Le rapport 
          est presente en personne au dirigeant et/ou a la DRH pour permettre un echange approfondi sur 
          les conclusions et la mise en oeuvre.
        </p>
      </div>
    </ServicePageLayout>
  );
}
